import { DisplayModeCard } from "@/components/form-items/DisplayModeCard";
import { Button } from "@/components/ui/button";
import { useDisplayMode } from "@/context/DisplayModeContext";
import { tWithKey } from "@/lib/i18n-fallback";
import { cn } from "@/lib/utils";
import { Lock } from "lucide-react";
import { ReactNode, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

// Exact class strings used by AbstractForm's lateral tabs (default model) — kept
// in sync so the width ghost measures the real tab column width.
const TAB_LIST_CLASS =
  "flex-col h-fit justify-start p-2 items-start gap-1 inline-flex border rounded-xl border-[hsl(220_17%_93%)] bg-[hsl(210_24%,_98%)]";
const TAB_TRIGGER_CLASS =
  "self-stretch h-9 px-3 py-2 rounded-md justify-start items-center gap-2 inline-flex data-[state=active]:text-white text-gray-500";
const TAB_VALUE_CLASS = "font-semibold font-['Inter']";

/**
 * Field labels inside a simplified section are shown in semibold uppercase
 * (targets both shadcn `<label>` elements and the `MaskableFieldHeader` label
 * div, which carries the `maskable-field-label` marker class).
 */
const SIMPLIFIED_LABEL_STYLE = cn(
  "[&_label]:font-semibold [&_label]:uppercase [&_label]:tracking-wide",
  "[&_.maskable-field-label]:font-semibold [&_.maskable-field-label]:uppercase [&_.maskable-field-label]:tracking-wide",
);

/** Hides every field label inside a section (the section title stands in for it). */
const SIMPLIFIED_LABEL_HIDDEN = cn(
  "[&_label]:hidden [&_.maskable-field-label]:hidden",
);

/**
 * A titled card grouping fields in the simplified ("Essentiel") view. Reusable
 * across sheets — each sheet fills it with its own curated fields. Field labels
 * are forced to semibold uppercase; pass `hideFieldLabels` to drop them entirely
 * (e.g. a single-field section whose title already names the field).
 */
export function SimplifiedSection({
  title,
  children,
  className,
  hideFieldLabels = false,
}: {
  title: string;
  children: ReactNode;
  className?: string;
  hideFieldLabels?: boolean;
}) {
  return (
    <section
      className={cn(
        "flex flex-col gap-4 rounded-xl border border-slate-200 bg-white p-6",
        SIMPLIFIED_LABEL_STYLE,
        hideFieldLabels && SIMPLIFIED_LABEL_HIDDEN,
        className,
      )}
    >
      <h2 className="text-lg font-bold uppercase tracking-wide text-primary font-['Raleway']">
        {title}
      </h2>
      {children}
    </section>
  );
}

/** Footer note explaining that advanced data is hidden in the Essentiel mode. */
export function SimplifiedFooterNote() {
  const { t } = useTranslation();
  const { setMode } = useDisplayMode();

  return (
    <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-6 py-4 text-sm text-slate-600">
      <Lock className="h-4 w-4 shrink-0 text-slate-400" aria-hidden />
      <p>
        {tWithKey(
          t,
          "displayMode.footer.before",
          "Les données avancées sont masquées en mode Essentiel. Pour y accéder, passez en",
        )}{" "}
        <button
          type="button"
          className="font-semibold text-primary underline underline-offset-2"
          onClick={() => setMode("pro")}
        >
          {tWithKey(t, "displayMode.footer.proLink", "mode Pro")}
        </button>
        {"."}
      </p>
    </div>
  );
}

/**
 * Invisible, off-screen replica of the Pro tab list. It is never shown; its
 * only purpose is to measure the exact width the lateral tab column has in the
 * complete view, so the simplified left column can match it (independently of
 * label length / translations).
 */
function TabListWidthGhost({
  labels,
  measureRef,
}: {
  labels: string[];
  measureRef: React.Ref<HTMLDivElement>;
}) {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute -left-[9999px] top-0"
    >
      {/* Real Button + exact tab classes so the measured width equals the
          complete view's tab column exactly. */}
      <div ref={measureRef} className={TAB_LIST_CLASS}>
        {labels.map((label, i) => (
          <Button key={i} variant="not_selected" className={TAB_TRIGGER_CLASS}>
            <p className={TAB_VALUE_CLASS}>{label}</p>
          </Button>
        ))}
      </div>
    </div>
  );
}

/**
 * Shell for a sheet's simplified view: a left column holding the display-mode
 * selector (kept in the same slot as the lateral tabs of the Pro view) and a
 * right column with the sheet's curated sections, followed by the footer note.
 * Sheet-agnostic — pass the sections as `children`.
 *
 * `tabLabels` (the Pro view's tab labels) lets the left column reserve the exact
 * same width as the complete view's tab column.
 */
export function SimplifiedFicheLayout({
  children,
  tabLabels,
}: {
  children: ReactNode;
  tabLabels?: string[];
}) {
  const ghostRef = useRef<HTMLDivElement>(null);
  const [columnWidth, setColumnWidth] = useState<number>();

  useLayoutEffect(() => {
    if (!tabLabels || tabLabels.length === 0 || !ghostRef.current) return;
    setColumnWidth(ghostRef.current.offsetWidth);
  }, [tabLabels]);

  return (
    <div className="pt-8 px-8 max-w-screen-2xl mx-auto">
      {tabLabels && tabLabels.length > 0 && (
        <TabListWidthGhost labels={tabLabels} measureRef={ghostRef} />
      )}
      <div className="grid grid-cols-[auto,1fr] gap-x-16">
        <div
          className="flex h-fit flex-col"
          style={columnWidth ? { minWidth: columnWidth } : undefined}
        >
          <DisplayModeCard />
        </div>
        <div className="flex w-full min-w-0 flex-col gap-6">
          {children}
          <SimplifiedFooterNote />
        </div>
      </div>
    </div>
  );
}

export default SimplifiedFicheLayout;
